import React, {useCallback, useState, useLayoutEffect} from "react";
import {useDropzone} from 'react-dropzone';
import styled from 'styled-components';
import Overlay from "../Overlay";
import Select from "react-select";
import {sweetalert} from "../../App";
import SimpleTable from "../tables/SimpleTable";
import {BatchSelect} from "./selects/BatchSelect";
import Batch from "../../types/Batch";
import TestsAPI from "../../network/TestsAPI";
import BatchesAPI from "../../network/BatchesAPI";
import ResultsAPI from "../../network/ResultsAPI";
import { isEmptyObject } from "jquery";
import ServicesAPI from "../../network/ServicesAPI";
import PaginationTool from "../PaginationTool";
import { isInTieredGroup } from "../../types/Service";


type MachineExportProps = {};

const getColor = (props) => {
    if (props.isDragAccept) {
        return '#00e676';
    }
    if (props.isDragReject) {
        return '#ff1744';
    }
    if (props.isDragActive) {
        return '#2196f3';
    }
    return '#eeeeee';
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${props => getColor(props)};
  border-style: dashed;
  background-color: rgba(236, 236, 236, 0.93);
  outline: none;
  transition: border .24s ease-in-out;
`;

export interface BatchInfo {
    SpecimenID: string
    InternalSpecimenID: string
    WellSlot: string
    ResultID: number
    ResultDescription: string
    PromptRerun: boolean
    ResultApprovedDate: Date
}

export const MachineExport = ({}: MachineExportProps) => {

    const [fileToUpload, setFileToUpload] = useState<Blob | {}>({})
    const [fileDroppedBool, setFileDroppedBool] = useState(false)
    const [showLoading, setShowLoading] = useState(true)
    const [batch, setBatch] = useState<Batch>(null)
    const [batchInfo, setBatchInfo] = useState<BatchInfo[]>([])
    const [viewClick, setViewClick] = useState(false)
    const [submitClick, setSubmitClick] = useState(false)
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null)
    const [tests, setTests] = useState([])
    const [results, setResults] = useState([])
    const [isEssentialOrComprehensive, setIsEssentialOrComprehensive] = useState(false)
    const [isDTC, setIsDTC] = useState(false)


    async function getData() {
        let services = await ServicesAPI.getAllServicesForMachineExport();
        if (!services.success) {
            return sweetalert.fire({
                title: 'Error',
                text: services.reason,
                icon: 'error',
                confirmButtonText: 'Ok'
            })
        }
        setServices(services.data.filter(s => s.ExportParseURL && s.IsEnabled))

        let results = await ResultsAPI.getAllResultsData();
        if (!results.success) {
            return sweetalert.fire({
                title: 'Error',
                text: results.reason,
                icon: 'error',
                confirmButtonText: 'Ok'
            })
        }
        setResults(results.data.results)
        setTests(results.data.tests)
    }

    useLayoutEffect(() => {
        getData();
    }, [])

    const onViewResults = async () => {

        if (!batch) {
            sweetalert.fire({icon: 'error', title: '', text: "Please select batch number"});
        } else if (isEmptyObject(fileToUpload)) {
            sweetalert.fire({icon: 'error', title: '', text: "Please select file to upload"});
        } else if (!selectedService) {
            sweetalert.fire({icon: 'error', title: '', text: "Please select a service"});
        }
         else {
            setShowLoading(true)
            let response = await BatchesAPI.postExportForParse(batch.ID, fileToUpload as Blob, selectedService.ID);
            setShowLoading(false);
            if (!response.success) {
                setFileDroppedBool(false)
                setBatchInfo([]);
                sweetalert.fire({icon: 'error', title: '', text: response.reason});
            } else {
                setBatchInfo(response.info);
                promptReruns(response.info);
            }
        }
    }

    async function promptReruns(data: BatchInfo[]) {

        for (const row of data) {
            let Queue = sweetalert.mixin({})
            if (!row.PromptRerun)
                continue
            await Queue.fire({
                title: 'Specimen is Inconclusive/Invalid',
                text: "Do you want to mark this specimen as a rerun? " + row.InternalSpecimenID,
                showCancelButton: true,
                showLoaderOnConfirm: true,
                confirmButtonText: 'Yes, mark as a rerun!',
                cancelButtonText: "No",
                showClass: {backdrop: 'swal2-noanimation'},
                allowOutsideClick: false
            }).then((result) => {
                if (result.isConfirmed) {
                    BatchesAPI.setRerun(batch.ID, row.SpecimenID).then(response => {
                        if (!response.success) {
                            sweetalert.fire({
                                title: 'Error',
                                text: response.reason,
                                icon: 'error',
                                confirmButtonText: 'Ok'
                            })
                        }
                    });
                }
            })
        }
        await sweetalert.fire(
            'Rerun check complete!',
            'This batch does not have anymore inconclusive/invalids',
            'success'
        )
        
        setViewClick(true);
    }

    const onUpdateResults = async () => {
        if (!batch) {
            sweetalert.fire({icon: 'error', title: '', text: "Please select batch number"});
        } else if (!selectedService) {
            sweetalert.fire({icon: 'error', title: '', text: "Please select a service"});
        }
        else if (isEmptyObject(fileToUpload)) {
            sweetalert.fire({icon: 'error', title: '', text: "Please select file to upload"});
        } else if (!batchInfo || batchInfo.length === 0) {
            sweetalert.fire({icon: 'error', title: '', text: "No data to upload"});
        } else {
            setShowLoading(true)
            let response = await BatchesAPI.postMachineResults(batch.ID, batchInfo);
            setShowLoading(false)
            if (!response.success) {
                await sweetalert.fire({
                    title: 'Error',
                    text: response.reason,
                    icon: 'error',
                    confirmButtonText: 'Ok'
                })
            } else {
                setFileDroppedBool(false);
                setBatchInfo([]);
                setSubmitClick(true)
                setBatch(null);
                await sweetalert.fire({
                    title: 'Success',
                    text: 'Results uploaded successfully',
                    icon: 'success',
                    confirmButtonText: 'Ok'
                })
            }
        }
    }

    const onDrop = useCallback(acceptedFiles => {
        acceptedFiles.forEach(async (file) => {
            setFileToUpload(file)
        })
        setFileDroppedBool(true)
    }, [])


    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
        acceptedFiles,
        fileRejections,
    } = useDropzone({
        accept: '.csv, application/vnd.ms-excel, text/csv',
        onDrop
    });

    const fileRejection = fileRejections.map(({file, errors}) => (
        <div key={(file as any).path}>
            {(file as any).path} - {file.size} bytes
            <ul>
                {errors.map(e => (
                    <li key={e.code}>{e.message}</li>
                ))}
            </ul>
        </div>
    ));
    
    const filepath = acceptedFiles.map(file => (
        <div key={(file as any).path}>
            {(file as any).path} - {file.size} bytes
        </div>
    ));

    function getFileComponent() {
        if (batchInfo.length > 0 || !batch)
            return null;

        if (fileDroppedBool) {
            if(filepath.length < 1){
                setFileDroppedBool(false)
                return sweetalert.fire({
                    title: 'File Rejected',
                    text: 'Check that file type is .csv',
                    icon: 'error',
                    confirmButtonText: 'Ok'
                })
            }
            return (
                <div className="row">
                    <div className="offset-2 col-8 text-center jumbotron"
                         style={{backgroundColor: 'transparent', border: '2px solid black'}}>
                        <div style={{fontStyle: 'bold', fontSize: '2em', marginBottom: '1em'}}>
                            {filepath.length > 0 ? "File to upload" : "Rejected"}
                        </div>
                        <div>{filepath}</div>
                    </div>
                </div>
            )
        } else {
            if(batch && batch.ResultApprovedDate){
                if(batch && batch.ResultApprovedDate){
                    sweetalert.fire({
                    title: 'Error',
                    text: "This Batch has already had results assigned to it.",
                    icon: 'error',
                    confirmButtonText: 'Ok'
                    }).then((result) => {
                        if(result.isConfirmed){
                            window.location.reload()
                        }
                    })
                }
            }
            return (
                <Container {...getRootProps({isDragActive, isDragAccept, isDragReject})}>
                    <input {...getInputProps()} />
                    <p>Drag and drop a file here or click to select files</p>
                </Container>)
        }
    }

    function clear(){
        setFileToUpload({})
        setFileDroppedBool(false)
        setBatch(null)
        setBatchInfo([])
        setViewClick(false)
        setSubmitClick(false)
        setSelectedService(null)
    }

    return (
        <div className="container-fluid  ">
            <PaginationTool />
            <Overlay show_loading={showLoading}/>
            <div className={"row"}>
                <div className="col-12 col-md-12 col-xl-8 col-lg-8  pt-2">
                    <div className="card mb-2">
                        <div className="card-header verlag-bold">
                            <h4>Machine Export</h4>
                        </div>
                        <div className="card-body">
                            <div className="form-group row">
                                <div className={"col-4"}>
                                    <label className="col-form-label">Service</label>
                                </div>
                                <div className={"col-8 pl-0 pr-1"}>
                                    <Select
                                        isSearchable={true}
                                        placeholder={"Please Select..."}
                                        onChange={(e) => {
                                            let selectedService = services.find(s => s.ID === e.value)
                                            if (selectedService?.ReportType === 5 ) {
                                                setIsDTC(true)
                                            } else if (selectedService && isInTieredGroup(selectedService, services)) {
                                                setIsEssentialOrComprehensive(true)
                                            }
                                            setSelectedService(selectedService)
                                            setBatch(null)
                                            setFileToUpload({})
                                            setFileDroppedBool(false)
                                        }}
                                        value={selectedService ? {label: selectedService.Name, value: selectedService.ID} : null}
                                        className={"state_select"}
                                        options={services.map(s => {return {label: s.Name, value: s.ID}})}
                                    />
                                </div>
                            </div>
                            <BatchSelect 
                                disabled={!selectedService}
                                onSelected={(e: { label, value: Batch }) => {
                                    setBatch(e.value)
                                    setFileToUpload({})
                                    setFileDroppedBool(false)
                                    
                                }}
                                filteredService={(b) => selectedService ? b.filter(batch => batch.ServiceID === selectedService.ID) : []}
                                value={batch && selectedService && selectedService.ID === batch.ServiceID ? 1 : null}
                                finishedLoading={() => {setShowLoading(false)}}
                            />
                            {getFileComponent()}
                            
                        </div>
                    </div>
                    {fileDroppedBool && filepath.length > 0 &&
                    <div className="mb-1 px-2 pt-2 pb-5" style={{border: '0.0625rem solid lightgray', borderRadius: '0.1875rem'}}>
                        <React.Fragment>
                            <button className="btn immySubmitButtonOutline" style={{float: 'left'}}
                                    onClick={() => onViewResults()}>View Results
                            </button>
                            <button className="btn immyClearButtonOutline ml-2" style={{float: 'left'}}
                                    onClick={() => clear()}>Clear Form
                            </button>
                            {viewClick &&
                            <button className="btn btn-outline-success" style={{float: 'right'}}
                                    onClick={() => onUpdateResults()}>Update Results
                            </button>}
                        </React.Fragment>
                    </div>
                    }
                </div>
                    
                {batchInfo.length > 0 && 
                    <div className="col-12 pt-2 pr-0 pl-0">
                        <a id={"top"}>{}</a>
                        <div className="card m-2" style={{marginBottom: '4rem'}}>
                
                            <div className="card-header verlag-bold stickToTop">
                                <h4 className={"text-center text-md-left"}>
                                    Records
                                    <section className="tableHeaderSection float-md-right d-flex justify-content-around">

                                        <h4 className={"float-right"}>
                                            Total: {batchInfo.length}
                                        </h4>
                                    </section>
                                </h4>
                            </div>

                            <div className="col-12 mb-3">
                                <div className="card mt-2 mb-1">
                                    <div className="card-body p-0 m-0 table-responsive">
                                        {isEssentialOrComprehensive || isDTC ? 
                                        <SimpleTable table_style="tableFixHead" columns={[
                                            {label: "Specimen ID", key: "SpecimenID"},
                                            {label: "Internal Specimen ID", key: "InternalSpecimenID"},
                                            {label: "Well Slot", key: "WellSlot"},
                                            {label: "Rerun", key: "RerunFlag", rawFormat: (val) => {
                                                return val.PromptRerun ? "Yes" : "No"
                                            }},
                                            {label: "Tests", key: "", rawFormat: (val) => {
                                                let labels = []
                                                if(val.TestsAndResultsMap && val.TestsAndResultsMap.length > 0){
                                                    let testName;
                                                    val.TestsAndResultsMap.map(t => {
                                                        testName = tests.find(f => f.ID === t.TestID).Name
                                                        labels.push(testName)
                                                    })
                                                    
                                                }
                                                return labels && labels.map(m => (
                                                    <p className="mb-0">{m}</p>
                                                ))
                                            }},
                                            {label: isDTC ? "Detection Level" : "Result Values", key: "", rawFormat: (val) => {
                                                let labels = []
                                                if(val.TestsAndResultsMap && val.TestsAndResultsMap.length > 0){
                                                    let resultValue;
                                                    val.TestsAndResultsMap.map(t => {
                                                        resultValue = t.ResultValue || t.ResultValue === 0 ? t.ResultValue : null
                                                        labels.push(resultValue)
                                                    })
                                                    
                                                }
                                                return labels && labels.map(m => (
                                                    <p className="mb-0">{Object.hasOwn(val.TestsAndResultsMap[0], 'ResultValue') ? m : 'N/A'}</p>
                                                ))
                                            }},
                                            {label: isDTC ? "Population Percentile" : "Relative Abundance", key: "", rawFormat: (val) => {
                                                let labels = []
                                                if(val.TestsAndResultsMap && val.TestsAndResultsMap.length > 0){
                                                    let relativeAbundance;
                                                    val.TestsAndResultsMap.map(t => {
                                                        relativeAbundance = t.RelativeAbundance || t.RelativeAbundance === 0 ? t.RelativeAbundance : null
                                                        labels.push(relativeAbundance)
                                                    })
                                                    
                                                }
                                                return labels && labels.map(m => (
                                                    <p className="mb-0">{Object.hasOwn(val.TestsAndResultsMap[0], 'RelativeAbundance') ? m : 'N/A'}</p>
                                                ))
                                            }}
                                        ]} table_data={batchInfo}/>
                                        : 
                                        <SimpleTable table_style="tableFixHead" columns={[
                                            {label: "Specimen ID", key: "SpecimenID"},
                                            {label: "Internal Specimen ID", key: "InternalSpecimenID"},
                                            {label: "Well Slot", key: "WellSlot"},
                                            {label: "Rerun", key: "RerunFlag", rawFormat: (val) => {
                                                return val.PromptRerun ? "Yes" : "No"
                                            }},
                                            {label: "Tests", key: "", rawFormat: (val) => {
                                                let labels = []
                                                if(val.TestsAndResultsMap && val.TestsAndResultsMap.length > 0){
                                                    let testName;
                                                    val.TestsAndResultsMap.map(t => {
                                                        testName = tests.find(f => f.ID === t.TestID).Name
                                                        labels.push(testName)
                                                    })
                                                    
                                                }
                                                return labels && labels.map(m => (
                                                    <p className="mb-0">{m}</p>
                                                ))
                                            }},
                                            {label: "Results", key: "", rawFormat: (val) => {
                                                let labels = []
                                                if(val.TestsAndResultsMap && val.TestsAndResultsMap.length > 0){
                                                    let resultName;
                                                    val.TestsAndResultsMap.map(t => {
                                                        resultName = results ? results.find(r => r.ID === t.ResultID).Value : '';
                                                        labels.push(resultName)
                                                    })
                                                    
                                                }
                                                return labels && labels.map(m => (
                                                    <p className="mb-0">{m}</p>
                                                ))
                                            }},
                                            {label: "Result Values", key: "", rawFormat: (val) => {
                                                let labels = []
                                                if(val.TestsAndResultsMap && val.TestsAndResultsMap.length > 0){
                                                    let resultValue;
                                                    val.TestsAndResultsMap.map(t => {
                                                        resultValue = t.ResultValue || t.ResultValue === 0 ? t.ResultValue : null
                                                        labels.push(resultValue)
                                                    })
                                                    
                                                }
                                                return labels && labels.map(m => (
                                                    <p className="mb-0">{Object.hasOwn(val.TestsAndResultsMap[0], 'ResultValue') ? m : 'N/A'}</p>
                                                ))
                                            }}
                                        ]} table_data={batchInfo}/>
                                        
                                        }
                                        
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                }
                
            </div>
           
        </div>
    );
}